/**
 * Wave 4 stage K (analytics): coarse, anonymous facts about the visit (pure helpers, tested).
 * Only a category leaves the browser: never the referrer, the user agent or the URL itself.
 */
import type { AnalyticsBrowser, AnalyticsDevice, AnalyticsSource } from "../../shared/analytics";

export function detectBrowser(ua: string): AnalyticsBrowser {
  if (/Edg\//.test(ua)) return "edge";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Chrome\/|CriOS\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

export function detectDevice(ua: string, touchPoints: number, shortSide: number): AnalyticsDevice {
  if (/iPad|Tablet/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)) return "tablet";
  if (/Mobi|iPhone|Android/.test(ua)) return /Android/.test(ua) && !/Mobi/.test(ua) ? "tablet" : "mobile";
  if (touchPoints > 1 && shortSide > 0 && shortSide < 900) return shortSide < 500 ? "mobile" : "tablet";
  return "desktop";
}

/**
 * Where the visit came from: an embedding game portal (iframe or `?src=portal`), Discord, a link
 * on another site, or our own site (direct visit, bookmark, same-site navigation).
 */
export function detectSource(search: string, referrer: string, inIframe: boolean, ownHost: string): AnalyticsSource {
  let tag = "";
  try {
    const p = new URLSearchParams(search);
    tag = (p.get("src") ?? p.get("utm_source") ?? p.get("ref") ?? "").toLowerCase();
  } catch {
    /* ignore */
  }
  if (tag === "portal" || inIframe) return "portal";
  if (tag.includes("discord")) return "discord";
  let host = "";
  try {
    host = referrer ? new URL(referrer).hostname.toLowerCase() : "";
  } catch {
    host = "";
  }
  if (host.includes("discord")) return "discord";
  if (tag === "link") return "link";
  if (!host || host === ownHost.toLowerCase()) return "site";
  return "link";
}

export interface AllowEnv {
  /** import.meta.env.MODE */
  mode: string;
  /** import.meta.env.DEV (Vite dev server: the owner's 5173 or an agent port). */
  dev: boolean;
  /** location.search */
  search: string;
  /** sessionStorage override set by `?analytics=on` in a dev tab (browser checks). */
  devOverride: boolean;
}

/**
 * Whether this page may send analytics at all: never in tests, never on the dev server (agent
 * and owner dev ports) unless a tab opted in with `?analytics=on`; `?analytics=off` always wins.
 * The server must also say it is enabled (it is off without ANALYTICS_ADMIN_KEY).
 */
export function analyticsAllowed(env: AllowEnv): boolean {
  if (env.mode === "test") return false;
  if (/(?:^|[?&])analytics=off\b/.test(env.search)) return false;
  if (env.dev) return env.devOverride || /(?:^|[?&])analytics=on\b/.test(env.search);
  return true;
}
